import React, { useEffect, useState } from "react";

type Props = {
  value: number;
  disabled?: boolean;
  /** Highlight when quantity differs from saved baseline. */
  changed?: boolean;
  /** Inclusive bounds (defaults: 1–999999, max 6 digits). */
  min?: number;
  max?: number;
  onChange: (next: number) => void;
};

const MAX_DIGITS = 6;

export function QuotationTableQtyStepper({
  value,
  disabled,
  changed,
  min = 1,
  max = 999999,
  onChange,
}: Props) {
  const safeVal = Number.isFinite(value) && value >= min ? Math.min(max, Math.floor(value)) : min;
  const [draft, setDraft] = useState(String(safeVal));

  useEffect(() => {
    setDraft(String(safeVal));
  }, [safeVal]);

  const commit = (raw: string) => {
    const digits = String(raw).replace(/\D/g, "").slice(0, MAX_DIGITS);
    if (digits === "") {
      onChange(min);
      setDraft(String(min));
      return;
    }
    const n = parseInt(digits, 10);
    if (!Number.isFinite(n) || n < min) {
      onChange(min);
      setDraft(String(min));
      return;
    }
    const next = Math.min(max, n);
    onChange(next);
    setDraft(String(next));
  };

  const dec = () => {
    if (disabled) return;
    if (safeVal > min) onChange(safeVal - 1);
  };
  const inc = () => {
    if (disabled) return;
    if (safeVal < max) onChange(safeVal + 1);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const digits = e.target.value.replace(/\D/g, "").slice(0, MAX_DIGITS);
    setDraft(digits);
    if (digits === "") return;
    const n = parseInt(digits, 10);
    if (!Number.isFinite(n) || n < min || n > max) return;
    // Reject leading-zero forms (e.g. "01") until blur normalizes.
    if (!/^[1-9]\d{0,5}$/.test(digits)) return;
    onChange(n);
  };

  const handleBlur = () => {
    if (disabled) return;
    commit(draft);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <div className={`vq-qtyStep${disabled ? " vq-qtyStep--disabled" : ""}`} role="group" aria-label="Quantity">
      <button
        type="button"
        className="vq-qtyStep__btn"
        disabled={disabled || safeVal <= min}
        onClick={dec}
        aria-label="Decrease quantity"
      >
        −
      </button>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={MAX_DIGITS}
        className={`vq-qtyStep__val vq-qtyStep__input${changed ? " vq-qtyStep__val--changed" : ""}`}
        value={draft}
        disabled={disabled}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        aria-label="Quantity"
        autoComplete="off"
      />
      <button
        type="button"
        className="vq-qtyStep__btn"
        disabled={disabled || safeVal >= max}
        onClick={inc}
        aria-label="Increase quantity"
      >
        +
      </button>
    </div>
  );
}
